import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Radio,
  Typography,
} from "antd";
import { useNavigateWithSearchParams } from "components/Assessment/hooks/useNavigateWithSearchParams";
import { PermissionIntegrationLogo } from "components/Integrations/IntegrationLogo";
import { DOCS_URLS } from "constants/docsUrls";
import { startCase } from "lodash";
import React, { useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { spacing } from "styles/spacing";

import { CONFIG_ROUTES_ROLES } from "../../App/roles";
import { useHasRole, useUser } from "../../Login/hook";
import { Splash } from "../../Splash";
import { Footer } from "../components/Footer";
import { SkipOnboardingButton } from "../components/SkipOnboardingButton";
import { WelcomePaths } from "../paths";
import { SupportedCloudProviders } from "../types";

const CloudProviderForm: React.FC = () => {
  const { orgSlug } = useParams();
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const handleSubmit = useCallback(
    (cloudProvider: { cloudStack: SupportedCloudProviders }) => {
      navigateWithSearchParams(
        `/o/${orgSlug}/${WelcomePaths.Root}/${cloudProvider.cloudStack}/`
      );
    },
    [navigateWithSearchParams, orgSlug]
  );

  return (
    <>
      <Typography.Paragraph>
        To get started, select the cloud provider you&apos;d like to integrate
        with P0 first (you can set up others later on):
      </Typography.Paragraph>

      <Form
        onFinish={handleSubmit}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        style={{ marginBottom: "20px" }}
        initialValues={{ cloudStack: "aws" }}
      >
        <Form.Item name="cloudStack">
          <Radio.Group
            style={{
              display: "flex",
              flexDirection: "column",
              gap: spacing.layout.grid.gap.sm,
            }}
            options={[
              {
                value: "aws",
                label: (
                  <>
                    {PermissionIntegrationLogo["aws"]}&nbsp;Amazon Web Services
                  </>
                ),
              },
              {
                value: "gcloud",
                label: (
                  <>
                    {PermissionIntegrationLogo["gcloud"]}&nbsp;Google Cloud
                    Platform
                  </>
                ),
              },
              {
                value: "azure",
                label: (
                  <>{PermissionIntegrationLogo["azure"]}&nbsp;Microsoft Azure</>
                ),
              },
            ]}
          />
        </Form.Item>
        <br />
        <Form.Item style={{ display: "flex", justifyContent: "center" }}>
          <Button htmlType="submit" type="primary">
            Submit
          </Button>
        </Form.Item>
        <Divider />
        <Flex
          justify="center"
          style={{ flexDirection: "column", alignItems: "center" }}
        >
          <SkipOnboardingButton />
        </Flex>
      </Form>
    </>
  );
};

const NoAccessMessage: React.FC = () => (
  <Typography.Paragraph>
    It seems you don&apos;t have access to the configuration page. Please
    contact support if you think this is an error.
    <br />
    <br />
    For now, you can get started exploring the app by going to the
    <SkipOnboardingButton textOverride="P0 Dashboard" />
  </Typography.Paragraph>
);

const Welcome = React.memo(() => {
  const { user } = useUser();
  const hasConfigRole = useHasRole(...CONFIG_ROUTES_ROLES);

  const userName = user?.displayName || undefined;

  return (
    <Splash overrideColumn>
      <Col xs={22} md={16} lg={14} xl={12}>
        <Card bordered={false}>
          <>
            <Typography.Title level={4}>
              Welcome to P0 Security
              {userName && `, ${startCase(userName)}`}!
            </Typography.Title>
            <Typography.Paragraph>
              Let&apos;s get set up! For the best onboarding experience, follow
              the steps in our&nbsp;
              <Link to={DOCS_URLS.QUICK_START} target="_blank" rel="noreferrer">
                quick start guide
              </Link>
              .
            </Typography.Paragraph>
          </>

          {hasConfigRole ? <CloudProviderForm /> : <NoAccessMessage />}

          <Footer />
        </Card>
      </Col>
    </Splash>
  );
});

Welcome.displayName = "Welcome";

export { Welcome };
